import React, { useCallback } from "react";
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Truck } from "lucide-react";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import {
  CreateShipmentData,
  UserSuggestion,
  Country,
  EntitySnapshot,
} from "../../types";
// Note: EntitySnapshot is still used as the shape returned by PartnerAutocomplete's
// onSnapshotSelect callback. No partner-link state is persisted on the shipment.
import {
  getErrorMessage,
  IMO_CLASS_OPTIONS,
  LOADTYPE_OPTIONS,
  LOADTYPE_OPTIONS_BY_MODE,
} from "../../constants/shipmentFormConstants";
import { Vehicle, VehicleSuggestion } from "../../types/vehicles";
import { User } from "../../types";
import { CarrierAutocomplete } from "../shipments/CarrierAutoComplete";
import { UserAutoComplete } from "../shipments/UserAutoComplete";
import { PartnerAutocomplete } from "../shipments/PartnerAutoComplete";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import Button from "../UI/Button";
import { useTranslation } from "react-i18next";
import { vehicleLabel } from "../../utils/vehicleLabel";

type CarrierField = "truckLicensePlate" | "trailerLicensePlate";
type UserField = "driver" | "admin" | "user";

interface TransportSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  setValue: UseFormSetValue<CreateShipmentData>;
  watch: UseFormWatch<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  users: User[];
  trucks: Vehicle[];
  trailers: Vehicle[];
  apiKey: string;
  countries: Country[];
  countryOptions: Array<{ value: string; label: string }>;
  /*
   * DEAD PROPS — kept commented for reference. Drove the legacy modal-based
   * Add/Edit-carrier flow. Replaced by inline fields rendered below the
   * PartnerAutocomplete in this component.
   *
   *   carrierSnapshot?: EntitySnapshot;
   *   onCarrierSnapshotSelect: (snapshot: EntitySnapshot) => void;
   *   onCarrierAdd: () => void;
   *   onCarrierEdit: () => void;
   *   onCarrierClear: () => void;
   */
  isEditMode: boolean;
  isDriverLocked?: boolean;
  loading: boolean;
  isAssignedLocal: boolean;
  handleAssignClick: () => Promise<void>;
  isGrouped?: boolean;
  persistedDriverId?: string | null;
}

export const TransportSection: React.FC<TransportSectionProps> = ({
  register,
  setValue,
  watch,
  errors,
  readonly,
  users,
  trucks,
  trailers,
  countries,
  countryOptions,
  apiKey,
  isEditMode,
  isDriverLocked,
  loading,
  isAssignedLocal,
  handleAssignClick,
  isGrouped,
  persistedDriverId,
}) => {
  const { t } = useTranslation("shipments");
  const watchTemperatureSensitive = watch("cargoHandling.temperatureSensitive");
  const watchAdr = watch("cargoHandling.adr");
  const watchFragile = watch("cargoHandling.fragile");
  const watchCarrierTrailerLicensePlate = watch("carrier.trailerLicensePlate");
  const watchCarrierTruckLicensePlate = watch("carrier.truckLicensePlate");
  const watchAssignedDriver = watch("assignedDriver");
  const watchCarrierCompanyName = watch("carrier.companyName");
  const watchCarrierStreet = watch("carrier.address.street");

  // Conditional visibility per transport mode:
  //   truck / trailer / driver  -> ROAD only
  //   container                 -> ROAD only here; for SEA the container number
  //                                field lives in the Sea Freight Details block,
  //                                hidden for AIR
  //   loadType labels           -> Full/Partial (ROAD), FCL/LCL (SEA), FTL/LTL (AIR)
  const transportMode = watch("transportMode") || "ROAD";
  const isRoad = transportMode === "ROAD";
  const showContainer = isRoad;
  const loadTypeOptions =
    LOADTYPE_OPTIONS_BY_MODE[transportMode] || LOADTYPE_OPTIONS;

  const handleCarrierSelect = useCallback(
    (carrier: VehicleSuggestion, field: CarrierField) => {
      if (field === "truckLicensePlate") {
        setValue("carrier.truckLicensePlate", carrier._id ?? "");
      } else {
        setValue("carrier.trailerLicensePlate", carrier._id ?? "");
      }
    },
    [setValue],
  );

  const handleUserSelect = useCallback(
    (user: UserSuggestion, field: UserField) => {
      if (field === "driver") {
        setValue("assignedDriver", user._id ?? "");
      }
    },
    [setValue],
  );

  const handleCarrierSnapshotSelect = useCallback(
    (snapshot: EntitySnapshot) => {
      const country = snapshot?.address?.country;
      setValue("carrier.companyName", snapshot.companyName);
      setValue("carrier.registerationCode", snapshot.registerationCode || "");
      setValue("carrier.phoneNumber", snapshot.phoneNumber || "");
      setValue("carrier.emailAddress", snapshot.emailAddress || "");
      setValue("carrier.address.street", snapshot.address.street);
      setValue("carrier.address.city", snapshot.address.city);
      setValue("carrier.address.state", snapshot.address.state);
      setValue("carrier.address.postalCode", snapshot.address.postalCode || "");
      setValue(
        "carrier.address.country",
        typeof country === "object" && country !== null ? country._id : country,
      );
    },
    [setValue],
  );

  const handleCarrierClear = useCallback(() => {
    setValue("carrier.companyName", "");
    setValue("carrier.registerationCode", "");
    setValue("carrier.phoneNumber", "");
    setValue("carrier.emailAddress", "");
    setValue("carrier.address.street", "");
    setValue("carrier.address.city", "");
    setValue("carrier.address.state", "");
    setValue("carrier.address.postalCode", "");
    setValue("carrier.address.country", "");
  }, [setValue]);

  const normalizePlateValue = (value: any, list: Vehicle[]): string => {
    if (!value) return "";
    if (typeof value === "object") return vehicleLabel(value);
    const found = list.find((v) => v._id === value);
    return (found && vehicleLabel(found)) || value;
  };

  const normalizeUserName = (value: any, list: User[]): string => {
    if (!value) return "";
    if (typeof value === "object")
      return `${value.firstName} ${value.lastName}` || "";
    const found = list.find((v) => v._id === value);
    if (found) {
      return `${found.firstName} ${found.lastName}`;
    }
    return value;
  };

  const handleCarrierAddressSelect = useCallback(
    (addressData: any) => {
      setValue("carrier.address.street", addressData.street);
      setValue("carrier.address.city", addressData.city);
      setValue("carrier.address.state", addressData.state);
      setValue("carrier.address.postalCode", addressData.postalCode);
      setValue("carrier.address.country", addressData.country);
    },
    [setValue],
  );

  const vehicleAndDriverLocked = readonly || !!isGrouped;
  const isDriverFromPlatform = users.some((u) => u._id === watchAssignedDriver);

  return (
    <SectionCard icon={Truck} title={t("transport.title")}>
      <div className="space-y-1">
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.loadType")}
          error={getErrorMessage(errors.loadType)}
        >
          <Select
            {...register("loadType")}
            error={!!errors.loadType}
            options={loadTypeOptions}
            disabled={readonly}
            placeholder={t("transport.loadType")}
            size="sm"
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.company")}
          error={getErrorMessage(errors.carrier?.companyName)}
        >
          <PartnerAutocomplete
            value={watchCarrierCompanyName || ""}
            onChange={(value) => setValue("carrier.companyName", value)}
            onSnapshotSelect={handleCarrierSnapshotSelect}
            onClear={handleCarrierClear}
            placeholder={t("transport.carrierCompanyPlaceholder")}
            disabled={readonly}
            error={!!errors.carrier?.companyName}
            addresses={false}
            type="carrier"
            size="sm"
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.regCode")}
          error={getErrorMessage(errors.carrier?.registerationCode)}
        >
          <Input
            {...register("carrier.registerationCode")}
            size="sm"
            error={!!errors.carrier?.registerationCode}
            placeholder={t("placeholders.regCode")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.phone")}
          error={getErrorMessage(errors.carrier?.phoneNumber)}
        >
          <Input
            {...register("carrier.phoneNumber")}
            size="sm"
            error={!!errors.carrier?.phoneNumber}
            placeholder={t("placeholders.phone")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.email")}
          error={getErrorMessage(errors.carrier?.emailAddress)}
        >
          <Input
            {...register("carrier.emailAddress")}
            type="email"
            size="sm"
            error={!!errors.carrier?.emailAddress}
            placeholder={t("placeholders.email")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.address")}
          error={getErrorMessage(errors.carrier?.address?.street)}
        >
          <AddressAutocompleteField
            apiKey={apiKey}
            value={watchCarrierStreet || ""}
            onChange={(value) => setValue("carrier.address.street", value)}
            onAddressSelect={handleCarrierAddressSelect}
            placeholder={t("transport.carrierAddressPlaceholder")}
            disabled={readonly}
            error={!!errors.carrier?.address?.street}
            countries={countries}
            size="sm"
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.city")}
          error={getErrorMessage(errors.carrier?.address?.city)}
        >
          <Input
            {...register("carrier.address.city")}
            size="sm"
            error={!!errors.carrier?.address?.city}
            placeholder={t("placeholders.city")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.state")}
          error={getErrorMessage(errors.carrier?.address?.state)}
        >
          <Input
            {...register("carrier.address.state")}
            size="sm"
            error={!!errors.carrier?.address?.state}
            placeholder={t("placeholders.state")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.postalCode")}
          error={getErrorMessage(errors.carrier?.address?.postalCode)}
        >
          <Input
            {...register("carrier.address.postalCode")}
            size="sm"
            error={!!errors.carrier?.address?.postalCode}
            placeholder={t("placeholders.postalCode")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.country")}
          error={getErrorMessage(errors.carrier?.address?.country)}
        >
          <Select
            {...register("carrier.address.country")}
            size="sm"
            error={!!errors.carrier?.address?.country}
            options={countryOptions}
            disabled={readonly}
            placeholder={t("placeholders.country")}
          />
        </FormField>

        {isRoad && (
          <>
            <FormField
              layout="inline"
              size="sm"
              label={t("labels.truck")}
              error={getErrorMessage(errors.carrier?.truckLicensePlate)}
            >
              <CarrierAutocomplete
                value={normalizePlateValue(
                  watchCarrierTruckLicensePlate,
                  trucks,
                )}
                vehicles={trucks}
                onChange={(value) =>
                  setValue("carrier.truckLicensePlate", value)
                }
                onCarrierSelect={(carrier) =>
                  handleCarrierSelect(carrier, "truckLicensePlate")
                }
                placeholder={t("transport.truckLicensePlate")}
                disabled={vehicleAndDriverLocked}
                searchField="truckLicensePlate"
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("labels.trailer")}
              error={getErrorMessage(errors.carrier?.trailerLicensePlate)}
            >
              <CarrierAutocomplete
                value={normalizePlateValue(
                  watchCarrierTrailerLicensePlate,
                  trailers,
                )}
                vehicles={trailers}
                onChange={(value) =>
                  setValue("carrier.trailerLicensePlate", value)
                }
                onCarrierSelect={(carrier) =>
                  handleCarrierSelect(carrier, "trailerLicensePlate")
                }
                placeholder={t("transport.trailerLicensePlate")}
                disabled={vehicleAndDriverLocked}
                error={!!errors.carrier?.trailerLicensePlate}
                searchField="trailerLicensePlate"
                size="sm"
              />
            </FormField>
          </>
        )}

        {showContainer && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.container")}
            error={getErrorMessage(errors.carrier?.containerNumber)}
          >
            <Input
              {...register("carrier.containerNumber")}
              error={!!errors.carrier?.containerNumber}
              placeholder={t("transport.containerNumber")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        )}

        {isRoad && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.driver")}
            error={getErrorMessage(errors.assignedDriver)}
          >
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <UserAutoComplete
                  value={normalizeUserName(watchAssignedDriver, users)}
                  users={users}
                  onChange={(value) => setValue("assignedDriver", value)}
                  onUserSelect={(user) => handleUserSelect(user, "driver")}
                  placeholder={t("transport.driverName")}
                  disabled={vehicleAndDriverLocked}
                  searchField="driver"
                  size="sm"
                />
              </div>
              {isEditMode &&
                !loading &&
                !isDriverLocked &&
                watchAssignedDriver &&
                isDriverFromPlatform &&
                watchAssignedDriver === persistedDriverId && (
                  <Button
                    onClick={handleAssignClick}
                    variant={isAssignedLocal ? "outline" : "primary"}
                    type="button"
                  >
                    {isAssignedLocal
                      ? t("transport.unassign")
                      : t("transport.assign")}
                  </Button>
                )}
            </div>
          </FormField>
        )}

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.specialHandling")}
        >
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-1">
            <label className="flex items-center gap-2 text-[12px] text-gray-600">
              <input
                {...register("cargoHandling.temperatureSensitive")}
                type="checkbox"
                disabled={readonly}
                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("transport.tempSensitive")}
            </label>
            <label className="flex items-center gap-2 text-[12px] text-gray-600">
              <input
                {...register("cargoHandling.adr")}
                type="checkbox"
                disabled={readonly}
                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("transport.adr")}
            </label>
            <label className="flex items-center gap-2 text-[12px] text-gray-600">
              <input
                {...register("cargoHandling.fragile")}
                type="checkbox"
                disabled={readonly}
                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("transport.fragileCargo")}
            </label>
          </div>
        </FormField>

        {watchTemperatureSensitive && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.tempRange")}
            error={getErrorMessage(errors.cargoHandling?.temperatureRange)}
          >
            <Input
              {...register("cargoHandling.temperatureRange")}
              error={!!errors.cargoHandling?.temperatureRange}
              placeholder={t("transport.tempPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        )}

        {watchAdr && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.imoClass")}
            error={getErrorMessage(errors.cargoHandling?.imoClass)}
          >
            <Select
              {...register("cargoHandling.imoClass")}
              error={!!errors.cargoHandling?.imoClass}
              options={IMO_CLASS_OPTIONS}
              disabled={readonly}
              placeholder={t("transport.selectImoClass")}
              size="sm"
            />
          </FormField>
        )}

        {watchFragile && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.fragileSpec")}
            error={getErrorMessage(errors.cargoHandling?.specifications)}
          >
            <Textarea
              {...register("cargoHandling.specifications")}
              error={!!errors.cargoHandling?.specifications}
              placeholder={
                watchFragile
                  ? t("transport.fragileHandling")
                  : t("transport.dangerousGoods")
              }
              readOnly={readonly}
              rows={2}
              size="sm"
            />
          </FormField>
        )}
      </div>
    </SectionCard>
  );
};

TransportSection.displayName = "TransportSection";
